import { type FireAnalyticsEvent } from '../../../common/types';
import { EVENT_SOURCE, SEARCH_DIALOG_EVENT_SOURCE } from '../../constants/analytics';

import { type CommonAttributes, type NonPrivacySafeAttributes } from './types';

/**
 * This distinguishes the source of the OAuth event in search.
 * More information: https://hello.atlassian.net/wiki/x/VZcRSQE
 */
export const OAuthSource = {
	quickFindFilter: 'quick-find-filter',
	quickFindOAuthBanner: 'quick-find-oauth-banner',
	quickFindBulkConnect: 'quick-find-bulk-connect',
	searchPageFilter: 'search-page-filter',
	searchPageOAuthBanner: 'search-page-oauth-banner',
	searchPageBulkConnect: 'search-page-bulk-connect',
} as const;

export type OAuthSource = (typeof OAuthSource)[keyof typeof OAuthSource];

interface OAuthEventProps {
	cloudId: string;
	product: string;
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	isOnSearchDialog?: boolean;
	oAuthSource: OAuthSource;
}

// https://data-portal.internal.atlassian.com/analytics/registry/71661
export const onOAuthStarted = ({
	cloudId,
	product,
	commonAttributes,
	nonPrivacySafeAttributes,
	isOnSearchDialog,
	oAuthSource,
}: OAuthEventProps): FireAnalyticsEvent => {
	return {
		source: isOnSearchDialog ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		action: 'clicked',
		actionSubject: 'thirdPartyOAuth',
		attributes: {
			additionalAttributes: {
				cloudId,
				product,
				oAuthSource,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
		eventType: 'track',
	};
};

// https://data-portal.internal.atlassian.com/analytics/registry/86359
export const onOAuthCompleted = ({
	cloudId,
	product,
	commonAttributes,
	nonPrivacySafeAttributes,
	isOnSearchDialog,
	oAuthSource,
}: OAuthEventProps): FireAnalyticsEvent => {
	return {
		source: isOnSearchDialog ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		action: 'completed',
		actionSubject: 'thirdPartyOAuth',
		attributes: {
			additionalAttributes: {
				cloudId,
				product,
				oAuthSource,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
		eventType: 'track',
	};
};

// https://data-portal.internal.atlassian.com/analytics/registry/86401
export const onOAuthFailed = ({
	cloudId,
	product,
	commonAttributes,
	nonPrivacySafeAttributes,
	isOnSearchDialog,
	oAuthSource,
}: OAuthEventProps): FireAnalyticsEvent => {
	return {
		source: isOnSearchDialog ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		action: 'failed',
		actionSubject: 'thirdPartyOAuth',
		attributes: {
			additionalAttributes: {
				cloudId,
				product,
				oAuthSource,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
		eventType: 'track',
	};
};
